import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {DynamicStatusButton} from 'shells/button/dynamic-status-button/dynamic-status-button';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {PageTitleBar} from '../../layout';
import {useGetZoomIntegration} from '../../scheduler/location/conferencing-modals/graphql/hooks/use-get-zoom-integration';
import {useSetZoomIsEnabled} from '../../scheduler/location/conferencing-modals/graphql/hooks/use-set-zoom-is-enabled';

import {
    getFlashBannerType,
    getPopoverContent,
    getServiceUrl,
    getStatusDescription,
    getStatusText,
    handleEnableZoom,
} from '../helpers';
import {PageContent} from '../page-content';

type Props = ContextRouter;

export function ZoomListPage(props: Props) {
    const [errorMessage, setErrorMesssage] = React.useState<string | null | undefined>(null);
    const {zoomIntegration, isLoading, refetch, error, startPolling, stopPolling} =
        useGetZoomIntegration();
    const {user} = useGetSessionUser();

    const zoomUserEmail = zoomIntegration ? zoomIntegration.email : '';
    const isZoomConnected = zoomIntegration ? zoomIntegration.isEnabled : false;
    const setZoomIsEnabled = useSetZoomIsEnabled();

    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

    const connectZoom = (onStart?: () => void, onSuccess?: () => void): void => {
        handleEnableZoom({
            setZoomIsEnabled,
            refetch,
            setError: setErrorMesssage,
            onAuthStart: onStart,
            onAuthSuccess: onSuccess,
        }).then((hasAuth) => {
            if (!hasAuth) {
                setZoomIsEnabled({isEnabled: false});
            }
        });
    };

    const disconnectZoom = () => {
        setZoomIsEnabled({isEnabled: false});
    };

    const handleReconfigure = () => {
        setZoomIsEnabled({isEnabled: false}).then(() => {
            appHistory.replace(getServiceUrl('ZOOM'));
        });
    };

    return (
        <MainContentContainer whiteBackground={true}>
            <PageTitleBar
                title='Zoom'
                subheaderPrimaryText={
                    isZoomConnected
                        ? 'View meeting details, transcriptions, and summaries all in one place'
                        : 'Connect Nutshell to your Zoom account for seamless collaboration'
                }
                buttons={[
                    NutClientConfig.hasZoomTranscription && isZoomConnected && (
                        <DynamicStatusButton
                            key='zoom'
                            text={getStatusText(isZoomConnected, error, 'ZOOM')}
                            headerType={getFlashBannerType(isZoomConnected, error)}
                            headerContent={getStatusDescription(isZoomConnected, error, 'ZOOM')}
                            isDialogOpen={isDialogOpen}
                            setIsDialogOpen={setIsDialogOpen}
                            popoverContent={getPopoverContent({
                                isEnabled: true,
                                errorMessage: error,
                                userEmail: zoomUserEmail,
                                handleReconfigure,
                                setIsDialogOpen,
                                serviceType: 'ZOOM',
                            })}
                        />
                    ),
                ]}
            />
            {!zoomIntegration && isLoading ? (
                <LoadingPage />
            ) : (
                <PageContent
                    errorMessage={errorMessage}
                    setError={setErrorMesssage}
                    refetch={refetch}
                    isConnected={isZoomConnected}
                    isEnabled={true}
                    userEmail={zoomUserEmail || ''}
                    isLoadingIntegration={isLoading}
                    serviceType='ZOOM'
                    startPolling={startPolling}
                    stopPolling={stopPolling}
                    user={user}
                    enableIntegration={connectZoom}
                    disableIntegration={disconnectZoom}
                    connectIntegration={connectZoom}
                    {...props}
                />
            )}
        </MainContentContainer>
    );
}
